import React, { useCallback, useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CompPagination, Container } from "components";
import { AiOutlinePlus } from "react-icons/ai";
import Breadcrumb from "components/molecules/Breadcrumb";
import { useDispatch, useSelector } from "react-redux";
import { deleteData, getData } from "actions";
import { API_URL_apiSetting, API_URL_ticketPriority } from "constants/api";
import { ticketPriorityReducers } from "redux/ticketPrioritySlice";
import { BiSortDown, BiSortUp } from "react-icons/bi";
import { FaEdit, FaTrash } from "react-icons/fa";
import useDebounce from "hooks/useDebounce";
import { SyncLoader } from "react-spinners";
import { Tooltip } from "react-tooltip";
import { AuthContext } from "context/AuthContext";
import axios from "axios";
import { toast } from "react-toastify";

// field for use sort up/down
const TABLE_HEAD = [
  { title: "Priority", field: "" },
  { title: "Priority description", field: "" },
  { title: "Priority color", field: "" },
  { title: "Status", field: "" },
  { title: "Actions", field: "" },
];

const Priorities = () => {
  const navigate = useNavigate();
  const [adminAccess, setAdminAccess] = useState(false);
  const { datauser } = useContext(AuthContext);
  const dispatch = useDispatch();
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [isOn, setIsOn] = useState("");

  const {
    getTicketPriorityResult,
    getTicketPriorityLoading,
    getTicketPriorityError,
    addTicketPriorityResult,
    deleteTicketPriorityResult,
  } = useSelector((state) => state.ticketPriority);

  const get = useCallback(
    async (params) => {
      getData(
        API_URL_ticketPriority,
        params,
        { dispatch, redux: ticketPriorityReducers },
        "GET_TICKETPRIORITY"
      );
    },
    [dispatch]
  );

  const onDelete = (item) => {
    deleteData(
      API_URL_ticketPriority + item.id,
      { dispatch, redux: ticketPriorityReducers },
      "DELETE_TICKETPRIORITY"
    );
  };

  const onEdit = (item) => {
    navigate(`/administrator/manage/priorities/edit`, {
      state: {
        item,
      },
    });
  };

  const handlePageClick = ({ selected }) => {
    const newOffset = selected * limit;
    const params = `?all=True&limit=${limit}&offset=${newOffset}&search=${search}&ordering=${
      sortOrder === "desc" ? "-" : ""
    }${sortColumn}`;
    setOffset(newOffset);
    setCurrentPage(selected);
    get(params);
  };

  const delayedSearch = useCallback(
    useDebounce((value) => {
      //RESET PAGINATE KE 1
      setOffset(0);
      setCurrentPage(0);
      const params = `?all=True&limit=${limit}&offset=${offset}&search=${value}&ordering=${
        sortOrder === "desc" ? "-" : ""
      }${sortColumn}`;
      get(params);
    }, 1000),
    [limit, offset, sortOrder, sortColumn]
  );

  const onSearch = (value) => {
    setSearch(value);
    delayedSearch(value);
  };

  const handleSort = (column) => {
    //RESET PAGINATE KE 1
    setOffset(0);
    setCurrentPage(0);
    if (column === sortColumn) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  // Sort icons
  const renderSortIcon = (field) => {
    if (field === sortColumn) {
      return sortOrder === "asc" ? (
        <BiSortUp className="ml-1 h-4" />
      ) : (
        <BiSortDown className="ml-1 h-4" />
      );
    }
    return <BiSortUp className="ml-1 h-4 text-gray-300" />;
  };

  const handleSelectLimit = (limit) => {
    //RESET PAGINATE KE 1
    setOffset(0);
    setCurrentPage(0);

    const params = `?all=True&limit=${limit}&offset=${offset}&search=${search}&ordering=${
      sortOrder === "desc" ? "-" : ""
    }${sortColumn}`;
    get(params);
    setLimit(limit);
  };

  const action = [
    {
      name: "Edit",
      icon: <FaEdit />,
      func: onEdit,
      colorlight: "bg-sky-500",
      colordark: "dark:bg-sky-800",
      hoverlight: "hover:bg-sky-600",
      hoverdark: "hover:dark:bg-sky-900",
    },
    {
      name: "Delete",
      icon: <FaTrash />,
      func: onDelete,
      colorlight: "bg-red-500",
      colordark: "dark:bg-red-800",
      hoverlight: "hover:bg-red-600",
      hoverdark: "hover:dark:bg-red-900",
    },
  ];

  const fetchData = useCallback(() => {
    const params = `?all=True&limit=${limit}&offset=${offset}&search=${search}&ordering=${
      sortOrder === "desc" ? "-" : ""
    }${sortColumn}`;
    get(params);
  }, [get, limit, offset, sortColumn, sortOrder]);

  const fetchIsOn = () => {
    axios({
      headers: {
        Authorization: `Bearer ${localStorage.getItem("sseccaxds")}`,
      },
      method: "GET",
      url: API_URL_apiSetting + "change_priority/",
    })
      .then((response) => {
        const data = response.data.value;
        const on = data === "1" ? true : false;
        setIsOn(on);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (addTicketPriorityResult) {
      fetchData();
    }
  }, [addTicketPriorityResult, fetchData]);

  useEffect(() => {
    if (deleteTicketPriorityResult) {
      fetchData();
    }
  }, [deleteTicketPriorityResult, fetchData]);

  const handleToggle = () => {
    setIsOn(!isOn);

    const on = !isOn ? "1" : "0";

    axios({
      headers: {
        Authorization: `Bearer ${localStorage.getItem("sseccaxds")}`,
      },
      method: "PUT",
      data: { value: on },
      url: API_URL_apiSetting + "change_priority/",
    })
      .then((response) => {
        toast.success("User priority status Updated", {
          position: "top-center",
          autoClose: true,
          hideProgressBar: true,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    if (datauser !== "") {
      fetchIsOn();
      const permissionViewMenuAdmin = datauser.group_permissions.includes(
        "access_menu_admin"
      );
      if (permissionViewMenuAdmin) {
        setAdminAccess(true);
      } else {
        navigate("/dashboard/agent");
      }
    }
  }, [datauser]);

  if (!adminAccess) {
    return null;
  }

  return (
    <Container>
      <Breadcrumb
        title={" Ticket priority"}
        excludedPaths={["manage", "administrator"]}
        group={"Superadmin"}
      />
      <div className="bg-neutral dark:bg-neutral-800 rounded overflow-hidden shadow">
        <div className="h-12 w-full grid items-center px-4 bg-neutral-100 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600 text-xs md:text-base">
          Priority
          <div className="col-end-7 flex justify-end">
            <Link
              to="/administrator/manage/priorities/create"
              className="flex items-center gap-1 p-1 px-3 rounded shadow-sm border bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-700 dark:border-neutral-600 dark:hover:bg-neutral-800"
            >
              <AiOutlinePlus />
              Create ticket priority
            </Link>
          </div>
        </div>

        <div className="h-20 w-full grid items-center px-4 bg-neutral dark:bg-neutral-800">
          <span
            className="text-blue-700 font-medium w-fit"
            data-tooltip-id="my-tooltip"
            data-tooltip-content="Active user can select the priority while creating ticket"
            data-tooltip-place="left"
          >
            User priority status
          </span>
          <div className="col-end-7 flex justify-end">
            <div className="flex items-center">
              <span className="mr-2">OFF</span>
              <button
                type="button"
                className={`w-12 h-6 flex items-center rounded-full px-1 duration-300 focus:outline-none ${
                  isOn ? "bg-green-400" : "bg-gray-300"
                }`}
                onClick={(e) => handleToggle(e)}
              >
                <span
                  className={`${
                    isOn ? "translate-x-6" : "translate-x-1"
                  } inline-block w-4 h-4 transform bg-white rounded-full duration-300`}
                />
              </button>
              <span className="ml-2">ON</span>
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row text-center md:justify-between align-middle px-4 mb-4 mt-1 bg-neutral dark:bg-neutral-800 ">
          <div className="p-1 inline-block align-middle">
            <select
              id="limit"
              value={limit}
              onChange={(e) => handleSelectLimit(e.target.value)}
              className="border rounded-sm p-1 border-gray-300 dark:text-black focus:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={500}>500</option>
            </select>
            <span className="mx-2 text-sm ">Records per page</span>
          </div>
          <div className="p-1 inline-block align-middle">
            <span className="text-sm mr-2">Search:</span>
            <input
              id="search"
              type="text"
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              placeholder="by priority"
              className="p-1 border border-gray-300 dark:text-black focus:border-blue-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>
        </div>

        {/* <CardContainer title="List of agents"> */}
        <div className="mx-4">
          <div className="overflow-x-auto min-h-[55vh]">
            <table className="border border-collapse items-center w-full">
              <thead className="text-left">
                <tr>
                  {TABLE_HEAD.map((item, itemIdx) => (
                    <th
                      key={itemIdx}
                      className="p-2 border text-sm whitespace-nowrap cursor-pointer"
                      onClick={() => {
                        item.field && handleSort(item.field);
                      }}
                    >
                      <span className="flex">
                        {item.title}
                        {item.field && renderSortIcon(item.field)}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* loading  */}
                {getTicketPriorityLoading && (
                  <tr>
                    <td
                      className="text-center py-12"
                      colSpan={TABLE_HEAD.length + 1}
                    >
                      <div className="pt-10 pb-6 flex justify-center items-center">
                        <SyncLoader color="#4477CE" />
                      </div>
                    </td>
                  </tr>
                )}
                {/* Error */}
                {getTicketPriorityError && (
                  <tr>
                    <td className="text-center" colSpan={TABLE_HEAD.length + 1}>
                      <div className="pt-20 pb-12 flex justify-center items-center text-xs text-red-500">
                        {getTicketPriorityError}
                      </div>
                    </td>
                  </tr>
                )}
                {/* Result = 0 */}
                {getTicketPriorityResult.results &&
                  getTicketPriorityResult.results.length === 0 && (
                    <tr>
                      <td
                        className="text-center"
                        colSpan={TABLE_HEAD.length + 1}
                      >
                        <div className="pt-20 pb-12 flex justify-center items-center text-xs text-slate-600 dark:text-white">
                          No Data
                        </div>
                      </td>
                    </tr>
                  )}

                {getTicketPriorityResult.results &&
                  getTicketPriorityResult.results.map((item, index) => (
                    <tr key={index}>
                      <td className="p-4 border border-blue-gray-50 w-1/5">
                        {item.priority}
                      </td>
                      <td className="p-4 border border-blue-gray-50 w-1/5">
                        {item.priority_desc}
                      </td>
                      <td className="p-4 border border-blue-gray-50 w-1/5">
                        <input
                          type="color"
                          value={item.priority_color}
                          className="bg-transparent"
                          readOnly
                        />
                      </td>
                      <td className="p-4 border border-blue-gray-50 w-1/5">
                        <p
                          className={`${
                            item.status === 1
                              ? "text-green-500"
                              : "text-red-600"
                          } `}
                        >
                          {item.status ? "Active" : "Disable"}
                        </p>
                      </td>
                      <td className="p-4 border border-blue-gray-50 w-1/5">
                        <div className="flex">
                          {action.map((action, actionIdx) => (
                            <button
                              key={actionIdx}
                              className={`mx-0.5 text-white text-sm ${action.colorlight} ${action.colordark} ${action.hoverlight} ${action.hoverdark} rounded p-1 `}
                              onClick={() => action.func(item)}
                            >
                              <div className="flex items-center">
                                {action.icon} {action.name}
                              </div>
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <div className="pt-5 py-11">
            <CompPagination
              handlePageClick={(e) => handlePageClick(e)}
              pageCount={
                getTicketPriorityResult.count > 0
                  ? getTicketPriorityResult.count
                  : 0
              }
              limit={limit}
              offset={offset}
              currentPage={currentPage}
            />
          </div>
        </div>
        <Tooltip id="my-tooltip" />
      </div>
    </Container>
  );
};

export default Priorities;
